import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    useGetMarathonStatisticsQuery,
    useGetMarathonTypesQuery,
    useImportMarathonResultsMutation,
    useClearMarathonStatisticsMutation,
    useLazyExportMarathonStatisticsQuery,
    useLazyGetTicketsForExportQuery,
} from "../api";
import { useNotification } from "../hooks/useNotification";
import Notification from "../components/ui/Notification";

const MarathonStatisticsPage: React.FC = () => {
    const { t } = useTranslation(["marathon_statistics"]);
    const { showError, showSuccess, notification, hideNotification } =
        useNotification();

    // Состояния фильтров
    const [selectedRound, setSelectedRound] = useState<number | null>(null);
    const [selectedType, setSelectedType] = useState<number | null>(null);
    const [csvText, setCsvText] = useState("");
    const [ticketsType, setTicketsType] = useState<number | null>(null);

    // Запросы данных
    const {
        data: statisticsData,
        isLoading: isLoadingStatistics,
        refetch: refetchStatistics,
    } = useGetMarathonStatisticsQuery({
        round: selectedRound || undefined,
        type: selectedType || undefined,
    });
    const { data: typesData } = useGetMarathonTypesQuery();

    // Мутации
    const [importResults, { isLoading: isImporting }] =
        useImportMarathonResultsMutation();
    const [clearStatistics, { isLoading: isClearing }] =
        useClearMarathonStatisticsMutation();

    // Lazy query для экспорта
    const [triggerExport, { isLoading: isExporting }] =
        useLazyExportMarathonStatisticsQuery();
    const [triggerTicketsExport, { isLoading: isLoadingTickets }] =
        useLazyGetTicketsForExportQuery();

    const handleImport = async () => {
        if (!selectedRound || !selectedType || !csvText.trim()) {
            showError(t("errors.fill_all_fields"));
            return;
        }

        try {
            const result = await importResults({
                round: selectedRound,
                type: selectedType,
                csv_text: csvText,
            }).unwrap();

            showSuccess(t("import_success", { count: result.imported_count }));
            setCsvText("");
            // Обновляем статистику после импорта
            await refetchStatistics();
        } catch (error: any) {
            const errorMessage = error?.data?.message || t("import_error");
            showError(errorMessage);
        }
    };

    const handleClear = async () => {
        if (!window.confirm(t("confirm_clear"))) {
            return;
        }

        try {
            const result = await clearStatistics({
                round: selectedRound || undefined,
                type: selectedType || undefined,
            }).unwrap();

            showSuccess(t("clear_success", { count: result.deleted_count }));
        } catch (error: any) {
            const errorMessage = error?.data?.message || t("clear_error");
            showError(errorMessage);
        }
    };

    const handleTicketsExport = async () => {
        if (!ticketsType) {
            showError("Выберите тип марафона");
            return;
        }

        try {
            const blob = await triggerTicketsExport({ type: ticketsType }).unwrap();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = downloadUrl;
            a.download = `marathon_tickets_type_${ticketsType}.csv`;
            a.click();
            window.URL.revokeObjectURL(downloadUrl);
            showSuccess("Файл CSV успешно загружен");
        } catch (error: any) {
            const errorMessage =
                error?.data?.message || error?.message || "Ошибка при загрузке билетов";
            showError(errorMessage);
        }
    };

    const handleExport = async () => {
        try {
            console.log("Export params:", {
                round: selectedRound,
                type: selectedType,
            });

            // Используем lazy query для экспорта
            const result = await triggerExport({
                round: selectedRound || undefined,
                type: selectedType || undefined,
            }).unwrap();

            console.log("Export result:", result);

            if (result.data && result.filename) {
                // Данные приходят в base64 (XLS)
                const binaryString = atob(result.data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], {
                    type: "application/vnd.ms-excel",
                });
                const downloadUrl = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = downloadUrl;
                a.download = result.filename;
                a.click();
                window.URL.revokeObjectURL(downloadUrl);
                showSuccess(t("export_success"));
            } else {
                console.log("No data in result:", result);
                showError(t("export_error"));
            }
        } catch (error: any) {
            console.error("Export error:", error);
            const errorMessage =
                error?.data?.message || error?.message || t("export_error");
            showError(errorMessage);
        }
    };

    const statistics = statisticsData?.data || [];
    const types = typesData?.data || [];

    return (
        <>
            <Notification
                message={notification.message}
                type={notification.type}
                isVisible={notification.isVisible}
                onClose={hideNotification}
            />

            <div className="container mx-auto px-4 py-8">
                <h1 className="text-3xl font-bold mb-8 text-gray-900 dark:text-gray-200">
                    {t("title")}
                </h1>

                {/* Фильтры */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-200">
                        {t("filters")}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {t("round")}
                            </label>
                            <select
                                value={selectedRound || ""}
                                onChange={(e) =>
                                    setSelectedRound(
                                        e.target.value
                                            ? Number(e.target.value)
                                            : null
                                    )
                                }
                                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2"
                            >
                                <option value="">{t("all_rounds")}</option>
                                <option value="1">{t("round_1")}</option>
                                <option value="2">{t("round_2")}</option>
                                <option value="3">{t("round_3")}</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {t("marathon_type")}
                            </label>
                            <select
                                value={selectedType || ""}
                                onChange={(e) =>
                                    setSelectedType(
                                        e.target.value
                                            ? Number(e.target.value)
                                            : null
                                    )
                                }
                                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2"
                            >
                                <option value="">{t("all_types")}</option>
                                {types.map((type: any) => (
                                    <option key={type.type} value={type.type}>
                                        {type.name} (${type.prize_amount})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-end space-x-2">
                            <button
                                onClick={handleExport}
                                disabled={isExporting}
                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                            >
                                {isExporting ? t("exporting") : t("export")}
                            </button>
                            <button
                                onClick={handleClear}
                                disabled={isClearing}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                            >
                                {t("clear")}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Экспорт билетов в CSV для стороннего приложения */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-200">
                        Экспорт билетов для стороннего приложения
                    </h2>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {t("marathon_type")}
                            </label>
                            <select
                                value={ticketsType || ""}
                                onChange={(e) =>
                                    setTicketsType(
                                        e.target.value ? Number(e.target.value) : null
                                    )
                                }
                                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2"
                            >
                                <option value="">{t("select_type")}</option>
                                {types.map((type: any) => (
                                    <option key={type.type} value={type.type}>
                                        {type.name} (${type.prize_amount})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <button
                                onClick={handleTicketsExport}
                                disabled={isLoadingTickets || !ticketsType}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isLoadingTickets
                                    ? t("loading")
                                    : "Скачать CSV с билетами"}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Импорт CSV */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-200">
                        {t("import_csv")}
                    </h2>
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t("round")}
                                </label>
                                <select
                                    value={selectedRound || ""}
                                    onChange={(e) =>
                                        setSelectedRound(
                                            e.target.value
                                                ? Number(e.target.value)
                                                : null
                                        )
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2"
                                >
                                    <option value="">
                                        {t("select_round")}
                                    </option>
                                    <option value="1">{t("round_1")}</option>
                                    <option value="2">{t("round_2")}</option>
                                    <option value="3">{t("round_3")}</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t("marathon_type")}
                                </label>
                                <select
                                    value={selectedType || ""}
                                    onChange={(e) =>
                                        setSelectedType(
                                            e.target.value
                                                ? Number(e.target.value)
                                                : null
                                        )
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2"
                                >
                                    <option value="">{t("select_type")}</option>
                                    {types.map((type: any) => (
                                        <option
                                            key={type.type}
                                            value={type.type}
                                        >
                                            {type.name} (${type.prize_amount})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {t("csv_data")}
                            </label>
                            <textarea
                                value={csvText}
                                onChange={(e) => setCsvText(e.target.value)}
                                placeholder={t("csv_placeholder")}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 h-32 resize-none"
                            />
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                {t("csv_format")}
                            </p>
                        </div>

                        <button
                            onClick={handleImport}
                            disabled={
                                isImporting ||
                                !selectedRound ||
                                !selectedType ||
                                !csvText.trim()
                            }
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                            {isImporting ? t("importing") : t("import")}
                        </button>
                    </div>
                </div>

                {/* Таблица результатов */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-200">
                        {t("results")}
                    </h2>

                    {isLoadingStatistics ? (
                        <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-2 text-gray-600 dark:text-gray-400">
                                {t("loading")}
                            </p>
                        </div>
                    ) : statistics.length === 0 ? (
                        <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                            {t("no_data")}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 dark:bg-gray-900">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t("position")}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t("name")}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Номер билета
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t("round_1")}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t("round_2")}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t("round_3")}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t("total_points")}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
                                    {statistics.map(
                                        (user: any, index: number) => (
                                            <tr
                                                key={user.user_id}
                                                className="hover:bg-gray-50 dark:bg-gray-900"
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                                    {index + 1}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                    {user.fio}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                    {user.ticket_id || user.user_id || "—"}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                    {user.round1}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                    {user.round2}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                    {user.round3}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-600">
                                                    {user.points}
                                                </td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default MarathonStatisticsPage;
